<script lang="ts">
	//UI
	import { AutoComplete, Card, Pagenation } from "@hitit/new_ui/src";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import FilterOption from "../component/Search/FilterOption.svelte";
	import SearchUserResult, {
		TableColumn,
	} from "../component/Search/SearchUserResult.svelte";

	//Utils
	import queryString from "query-string";
	import { navigate } from "svelte-routing";
	import { api, t } from "@hitit/hub/src/stores";
	import { translateCount } from "@hitit/translations";
	import { globalHistory } from "svelte-routing/src/history";
	//Types
	type locationOverload = { search: string; pathname: string };
	import type { UserMinDto } from "@hitit/api";
	import type { SortOrder } from "../component/Search/SearchUserResult.svelte";
	import { onDestroy } from "svelte";
	import { popup } from "@hitit/hub/src/v7/store";
	//Props
	//Constants
	const theFirstQuery = parseQuerystring(window.location.search);
	const divideCountOption = new Array(10)
		.fill(null) // 10 ~ 100
		.map((_, i) => i + 1)
		.map((v) => v * 10);
	//Variables
	let divideCount = theFirstQuery.divide;
	let users: UserMinDto[] = [];
	let page = theFirstQuery.page;
	let loading = true;
	let searchValue = theFirstQuery.search;
	let searchText = theFirstQuery.search;
	let sortOrder: SortOrder = theFirstQuery.sortOrder;
	let selectedSortColumn = theFirstQuery.sortColumn;
	$: totalCount = users.length;
	$: autoCompleteProps = {
		placeholder: $t("mbr.search.search_name_email"),
		searchCallback: autocompleteHandler,
	};
	$: maxPage = divideCount !== 0 ? Math.ceil(users.length / divideCount) : 0;
	$: sortedUsers = sort(users, selectedSortColumn, sortOrder);
	$: dividedUsers = divideUsers(sortedUsers, divideCount, page);
	//Functions
	async function autocompleteHandler(search: string) {
		try {
			const { data } = await $api.getUserlistByUsername(search, 0);

			const mappedResult = data.userMinDTOs.map((v) => ({
				name: v.name,
				callback: () => navigate(`/users/${v.id}/`),
			}));
			return mappedResult;
		} catch (e) {
			console.log("error");
			return [];
		}
	}
	function sort(list: UserMinDto[], column: TableColumn, by: SortOrder) {
		if (by === "none") return list;
		let compare: any;

		const compareFuncs = {
			asc: (a: any, b: any) => (a < b ? -1 : b < a ? 1 : 0),
			desc: (a: any, b: any) => (a > b ? -1 : b > a ? 1 : 0),
		};

		compare = compareFuncs[by];
		return [...list].sort((a, b) => {
			if (column === TableColumn.createddate) {
				const A = new Date(a.createddate);
				const B = new Date(b.createddate);

				return compare(A, B);
			} else {
				return compare(
					a[column as keyof UserMinDto],
					b[column as keyof UserMinDto]
				);
			}
		});
	}

	async function getAllUserByName(search = "") {
		try {
			loading = true;
			const { totalPages, userMinDTOs } = (
				await $api.getUserlistByUsername(search, 0)
			).data;
			if (totalPages === 0) {
				resetAll();
				return;
			}

			const dataPromise = new Array(totalPages - 1).fill(null).map((_, i) => {
				return $api.getUserlistByUsername(search, i + 1);
			});
			const afterPageOneUsers = (await Promise.all([...dataPromise]))
				.map((v) => {
					return v.data.userMinDTOs;
				})
				.flat(1);
			const allUsers = [...userMinDTOs, ...afterPageOneUsers];
			resetAll();
			users = allUsers;
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}
	function rowClickHandler(userId: string) {
		navigate(`/users/${userId}/`);
	}
	function enumrateSortBy(sortBy: SortOrder) {
		if (sortBy === "none") return "asc";
		else if (sortBy === "asc") return "desc";
		else return "none";
	}

	function sortClickHandler(selected: TableColumn) {
		const _newsortOrder =
			selected === selectedSortColumn ? enumrateSortBy(sortOrder) : "asc";
		navigateController(
			undefined,
			undefined,
			undefined,
			selected,
			_newsortOrder
		);
	}
	function resetAll() {
		users = [];
		page = 0;
	}
	function divideUsers(
		users: UserMinDto[],
		divideCount: number,
		currentPage: number
	) {
		const start = divideCount * currentPage;
		const end = start + divideCount;
		return users.slice(start, end);
	}
	function undefinedArray(length: number) {
		return new Array(length).fill(undefined);
	}

	function navigateController(
		_searchText = searchText,
		_page = page,
		_divideCount = divideCount,
		_sortColumn = selectedSortColumn,
		_sortOrder = sortOrder
	) {
		const searchQuery = `search=${_searchText}&`;
		const pageQuery = `page=${_page}&`;
		const divideQuery = `divide=${_divideCount}&`;
		const sortColumnQuery = `sortColumn=${_sortColumn}&`;
		const sortOrder = `sortOrder=${_sortOrder}`;

		const query =
			searchQuery + pageQuery + divideQuery + sortColumnQuery + sortOrder;
		navigate(`/users?` + query);
	}
	function parseQuerystring(query: string) {
		const parsed = queryString.parse(query);
		const { search, page, divide, sortColumn, sortOrder } = parsed;

		const dividedVerification = new Array(10)
			.fill(null)
			.map((_, i) => (i + 1) * 10)
			.find((v) => v === Number(divide));

		const sortColumnVerification = Object.values(TableColumn).find(
			(v) => v === sortColumn
		);
		const sortOrderVerification = ["none", "asc", "desc"].find(
			(v) => v === sortOrder
		);

		return {
			search: (search || "") as string,
			page: Number(page) || 0,
			divide: dividedVerification ? dividedVerification : 20,
			sortColumn: sortColumnVerification
				? sortColumnVerification
				: TableColumn.none,
			sortOrder: (sortOrderVerification
				? sortOrderVerification
				: "none") as SortOrder,
		};
	}

	$: if (page > maxPage && !loading) {
		const newPage = maxPage - 1 < 0 ? 0 : maxPage - 1;
		page = newPage;
	}
	//Do
	$: getAllUserByName(searchText);
	onDestroy(() => {
		$popup = undefined;
	});

	globalHistory.listen((v) => {
		const query = parseQuerystring((v.location as locationOverload).search);
		searchText = query.search;
		page = query.page;
		divideCount = query.divide;
		selectedSortColumn = query.sortColumn;
		sortOrder = query.sortOrder as SortOrder;
	});
</script>

{#if loading}
	<ContainerLoading />
{/if}
<div class="container">
	<form
		class="search-box"
		on:submit|preventDefault={() => navigateController(searchValue, 0)}
	>
		<AutoComplete {...autoCompleteProps} bind:search={searchValue} />
	</form>
	<Card noPadding>
		<div class="search-container">
			<div class="total-filteroption">
				<div class="total">
					{translateCount($t("global.total_{count}_people"), totalCount)}
				</div>
				<div class="filteroption">
					<FilterOption
						on:change={(e) =>
							navigateController(...undefinedArray(2), e.detail)}
						value={divideCount}
						items={divideCountOption}
						label={$t("mbr.search.display_count")}
					/>
				</div>
			</div>
			<div class="results">
				<SearchUserResult
					rowClickHandler={rowClickHandler}
					columnHeadClickHandler={sortClickHandler}
					sortBy={selectedSortColumn}
					sortOrder={sortOrder}
					users={dividedUsers}
				/>
				<Pagenation
					totalPage={maxPage}
					presentPage={page}
					buttonCallback={(p) => navigateController(undefined, p)}
				/>
			</div>
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../variables.scss";
	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		padding-top: 60px;
		.search-box {
			width: 100%;
		}

		.search-container {
			width: 100%;
			display: flex;
			flex-direction: column;
			padding: 20px 10px;
			gap: 20px;
			.results {
				display: flex;
				flex-direction: column;
				gap: 10px;
			}

			.total-filteroption {
				display: flex;
				flex-direction: column;
				gap: 10px;
				width: 100%;

				.total {
					width: 100%;
					flex-grow: 1;
					text-align: right;
					font-weight: 500;
					font-size: 1.25rem;
					color: #46474b;
				}
				.filteroption {
					display: flex;
					flex-direction: column;
					gap: 10px;
				}
			}
		}
	}
	@include DesktopSize {
		.container {
			padding-top: 30px;
			.search-box {
				width: 50%;
			}

			.search-container {
				padding: 30px 30px 35px 30px;
				gap: 33px;
				.results {
					gap: 20px;
				}

				.total-filteroption {
					flex-direction: row-reverse;
					align-items: center;
					flex-wrap: wrap;
					gap: 20px 0px;

					.total {
						width: auto;
					}

					.filteroption {
						flex-grow: 1;
						flex-direction: row;
					}
				}
			}
		}
	}
</style>
